import { useRecoilValue } from 'recoil';
import { currentDatabaseSelector, selectedDatabaseIdState } from '@transport-insights/uikit';
import { useTemporaryTrafficManagementCostConfidence } from '@src/modules/performance/context/temporary-traffic-management-api-hooks';
import { Text } from '@chakra-ui/react';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: null,
  content: [
    {
      title: 'Confidence in TTM Cost',
      description:
  <Text fontSize="inherit" mb={2}>
    The change in confidence level for each quarter.
    Also shows the confidence level for the peer group and the 25th-75th percentile confidence levels for all RCAs.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    All results provided by the RCAs.
  </Text>,
    },
  ],
};

function TemporaryTrafficManagementCostConfidence() {
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Percentile'];
  }

  // Cost confidence data
  const { data: costConfidenceData, isLoading } = useTemporaryTrafficManagementCostConfidence();
  const costConfidenceQuarters = [
    ...new Set(costConfidenceData?.costConfidences?.map((item) => `Q${item.quarter} ${item.lockYear}`)),
  ].sort();
  const costConfidenceTrendData = [
    {
      name: currentDatabase?.name,
      type: 'line',
      data: costConfidenceData?.costConfidences?.map((item, index) => ({ x: costConfidenceQuarters[index], y: parseInt(item.costConfidence, 10) })),
      color: CHART_COLOURS.blue,
    },
    {
      name: '25-75th Percentile',
      type: 'rangeArea',
      data: costConfidenceData?.nationalPercentiles?.map((item, index) => ({ x: costConfidenceQuarters[index], y: [item.percentile25th, item.percentile75th] })),
      color: CHART_COLOURS.lightgrey,
    },
  ];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const chartData = filterChartDataByName(
    costConfidenceTrendData,
    chartFilters,
  );

  return (
    <ReportSection
      title="Confidence in TTM Cost"
      informationSource={informationSource}
      isLoading={isLoading}
      lockYear={costConfidenceData?.costConfidences?.at(-1)?.lockYear}
      quarter={costConfidenceData?.costConfidences?.at(-1)?.quarter}
      quarterFormat="ytd"
    >
      <LineRangeAreaChart
        title=""
        categories={costConfidenceQuarters}
        series={chartData}
        width="100%"
        height="280"
        prefix=""
        suffix=""
        markers={{
          size: [3, 0],
        }}
        stroke={{
          curve: 'straight',
          width: [2, 0],
        }}
        decimalPoints={0}
        yAxisLabel="Cost Confidence"
        min={1}
        max={5}
      />
    </ReportSection>
  );
}

export default TemporaryTrafficManagementCostConfidence;
